<template>
  <NewBsBox
    :bs="{
      'cmp.inputGroup': 'wrapper',
      ...bs,
    }"
    :context="$props"
    v-bind="attrs.wrapper"
    @click="attrs.onClick"
  >
    <div
      v-if="$slots.left"
      ref="left"
      class="slot-left"
    >
      <!-- @slot 左侧 widget，如图标，按钮 -->
      <slot
        :width="leftWidth"
        name="left"
      />
    </div>

    <template v-if="$slots.customWrapper">
      <!-- @slot 自定义 input 的容器和内容 -->
      <slot
        :rightWidth="rightWidth"
        :leftWidth="leftWidth"
        name="customWrapper"
      />
    </template>
    <template v-else-if="$slots.custom">
      <NewBsBox
        :data-error="$attrs['data-error'] || null"
        :data-active="$attrs['data-active'] || null"
        :disabled="$attrs.disabled || null"
        :data-testid="$attrs['data-testid']"
        :context="{ bordered }"
        :bs="mergedInputBs"
      >
        <!-- @slot 自定义 input 内容, 带默认容器样式 -->
        <slot name="custom" />
      </NewBsBox>
      <slot name="input-footer" />
    </template>
    <template v-else-if="readonly">
      <NewBsBox
        :context="{ size, bordered }"
        :bs="{
          ...mergedInputBs,
          cursor: 'text',
          lineHeight: '32px',
          whiteSpace: 'nowrap',
          overflow: 'auto'
        }"
      >
        {{ $attrs.value }}
      </NewBsBox>
    </template>
    <template v-else>
      <NewBsBox
        ref="input"
        :context="{ size, bordered }"
        :bs="mergedInputBs"
        :readonly="inputReadonly || null"
        v-bind="attrs.input"
        as="input"
        @keyup="handleEnter"
      />
      <!-- @slot input 边上的兄弟节点，特殊场景保留使用 -->
      <slot name="input-footer" />
    </template>
    <div
      v-if="$slots.right"
      ref="right"
      class="slot-right"
    >
      <!-- @slot 右侧 widget，如图标，按钮，字数统计 -->
      <slot
        :width="rightWidth"
        name="right"
      />
    </div>
  </NewBsBox>
</template>

<script lang="ts">
  import { ComponentPublicInstance, defineComponent, PropType } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import initStyle from './style'
  import { BeerStyleObj } from '../../../core'

  export type InputSize = 's' | 'm' | 'l'

  export type FocusOption = {
    cursor?: 'start' | 'end' | 'select'
  }

  initStyle()
  /**
   * 组合 Input 样式，适合配置 left/right 内容块，并自动计算宽度适配;
   * 被各种 Input, Select, Picker 等组件使用
   */
  export default defineComponent({
    name: 'InputGroup',
    components: {
      NewBsBox,
    },
    inheritAttrs: false,
    props: {
      /**
       * @ignore
       *  容器 Bs
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @version 4.1.0
       * 是否带边框
       */
      bordered: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否只读显示
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * @ignore
       */
      inputReadonly: {
        type: Boolean,
        default: false,
      },
      /**
       * - 'm' as medium
       * - 'l' as large，比如 large search 场景
       * @type {s|m|l}
       */
      size: {
        type: String as PropType<InputSize>,
        default: 'm',
      },
      /**
       * @ignore
       *  input 组件的 Bs
       */
      inputBs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 输入框 padding 的偏离值
       */
      inputOffset: {
        type: Number,
        default: 11,
      },
      /**
       * 是否在回车后失去焦点
       */
      blurOnEnter: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否在挂载的时候自动focus
       */
      autoFocus: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        leftWidth: 0,
        rightWidth: 0,
        isUpdated: false,
      }
    },
    computed: {
      attrs(): any {
        const {
          onClick,
          role,
          'aria-label': ariaLabel,
          ...rest
        } = this.$attrs
        return {
          input: rest,
          onClick: (e: Event) => {
            if (!onClick || this.readonly || rest.disabled) {
              return null
            }
            return (onClick as any)(e)
          },
          wrapper: {
            role,
            'aria-label': ariaLabel,
          },
        }
      },
      mergedInputBs(): BeerStyleObj {
        const bs: BeerStyleObj = {
          'cmp.inputGroup': 'input',
          'cmp.inputGroup.inputWrapper': true,
          'cmp.inputGroup.inputSize': this.size,
          pl: this.leftWidth > 0 ? this.leftWidth + this.inputOffset - 1 : this.inputOffset,
          pr: this.rightWidth > 0 ? this.rightWidth + this.inputOffset - 1 : this.inputOffset,
          ...this.inputBs,
        }
        if (this.readonly) {
          bs.border = 'none'
        }
        return bs
      },
    },
    mounted() {
      // someway to wait class & its style applied
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.updateInputWith()
          if (this.autoFocus) {
            this.focus()
          }
        })
      })
    },
    updated() {
      this.updateInputWith()
    },
    methods: {
      handleEnter(e: KeyboardEvent) {
        if (
          !e.shiftKey
          && this.blurOnEnter
          && e.keyCode === 13
          && (e.target as HTMLInputElement).value) {
          this.blur()
        }
      },
      focus(option: FocusOption = {}) {
        if (this.$refs.input) {
          const cmp = this.$refs.input as ComponentPublicInstance
          const el = cmp.$el
          if (option.cursor === 'select') {
            el.setSelectionRange(0, el.value.length)
          }
          if (option.cursor === 'start') {
            el.setSelectionRange(0, 0)
          }
          if (option.cursor === 'end') {
            el.setSelectionRange(el.value.length, el.value.length)
          }
          el.focus()
        }
      },
      blur() {
        if (this.$refs.input) {
          (this.$refs.input as ComponentPublicInstance).$el.blur()
        }
      },
      updateInputWith() {
        if (!this.isUpdated && this.$el.offsetParent === null) {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.updateInputWith()
            })
          })
          return
        }
        if (this.$refs.left) {
          const { clientWidth: leftWidth } = this.$refs.left as HTMLElement
          // small threshold to avoid intensive update
          if (Math.abs(leftWidth - this.leftWidth) > 2) {
            this.leftWidth = leftWidth
          }
        }
        if (this.$refs.right) {
          const { clientWidth: rightWidth } = this.$refs.right as HTMLElement
          // small threshold to avoid intensive update
          if (Math.abs(rightWidth - this.rightWidth) > 2) {
            this.rightWidth = rightWidth
          }
        }
        if (!this.isUpdated) {
          this.isUpdated = true
        }
      },
    },
  })
</script>
